import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StandardAppLayout } from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import {
    asEngineViolations,
    engineErrorMessage,
    type EngineError,
    type SchemaViolation,
} from 'src/shared/state/api-slices/engineErrorMessage';
import { useGetStacItemQuery, useUpdateStacItemMutation, type StacItem } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import ItemForm from './components/item-form/ItemForm';
import DocumentModes from './components/document-editor/DocumentModes';
import { useJsonHatch } from './components/document-editor/useJsonHatch';
import { formFromItem, toItemDocument } from './domain/documents/stacItemDocument';

type LoadedItemEditProps = {
    item: StacItem;
    collectionId: string;
    isSaving: boolean;
    submitError?: string;
    submitViolations?: SchemaViolation[];
    onSave: (item: StacItem) => void;
    onCancel: () => void;
};

/**
 * The edit itself, once the version has arrived.
 *
 * Its own component because the JSON hatch holds state, and state cannot be held behind a
 * loading branch — the hooks would run on some renders and not others.
 */
const LoadedItemEdit = ({ item, collectionId, isSaving, submitError, submitViolations, onSave, onCancel }: LoadedItemEditProps) => {
    const { values, setValues, baseline, generation, apply } = useJsonHatch(
        formFromItem(item),
        document => formFromItem(document as StacItem),
        item
    );

    return (
        <DocumentModes noun='version' buildDocument={() => toItemDocument(values, collectionId, baseline as StacItem)} onApply={apply}>
            <ItemForm
                key={generation}
                mode='edit'
                initialValues={values}
                isSaving={isSaving}
                submitError={submitError}
                submitViolations={submitViolations}
                onValuesChange={setValues}
                onSubmit={next => onSave(toItemDocument(next, collectionId, baseline as StacItem))}
                onCancel={onCancel}
            />
        </DocumentModes>
    );
};

/**
 * Correcting a published version.
 *
 * The stored document is the base, so an edit changes what the form owns and leaves
 * the rest exactly as catalogued — with `created` and `updated` stripped, since both
 * belong to the engine and it would discard ours anyway.
 */
const StacItemEdit = () => {
    const { collectionId = '', itemId = '' } = useParams<{ collectionId: string; itemId: string }>();
    const navigate = useNavigate();
    const { data: item, isLoading } = useGetStacItemQuery({ collectionId, itemId });
    const [updateItem, { isLoading: isSaving }] = useUpdateStacItemMutation();
    const [submitError, setSubmitError] = useState<string | undefined>();
    /** Where the engine says the document is wrong, so the form can mark those inputs. */
    const [submitViolations, setSubmitViolations] = useState<SchemaViolation[]>([]);

    const save = async (next: StacItem) => {
        setSubmitError(undefined);
        setSubmitViolations([]);

        try {
            await updateItem({ collectionId, item: next }).unwrap();
            navigate(`/admin/catalogue/${collectionId}`);
        } catch (error) {
            setSubmitError(engineErrorMessage(error as EngineError));
            setSubmitViolations(asEngineViolations(error as EngineError));
        }
    };

    const mainContent = (() => {
        if (isLoading || !item) {
            return (
                <div className='flex justify-center py-24' data-testid='stac-item-edit-loading'>
                    <Loader />
                </div>
            );
        }

        return (
            <LoadedItemEdit
                item={item}
                collectionId={collectionId}
                isSaving={isSaving}
                submitError={submitError}
                submitViolations={submitViolations}
                onSave={save}
                onCancel={() => navigate(`/admin/catalogue/${collectionId}`)}
            />
        );
    })();

    return <StandardAppLayout title={`Edit ${itemId}`} mainContent={mainContent} />;
};

export default StacItemEdit;
